/**
 * CmsNavPage — Nav Builder
 *
 * Allows the tenant to manage the customer portal navigation:
 * - Create nav items linking to CMS pages, collection types, or external URLs
 * - Reorder via drag-and-drop (@dnd-kit/sortable)
 * - Group items under section labels
 * - Toggle visibility per item
 * - Delete items
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Navigation,
  Plus,
  Trash2,
  GripVertical,
  Eye,
  EyeOff,
  FileText,
  Database,
  ExternalLink,
  Pencil,
  ChevronLeft,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface NavItem {
  id: string;
  menuId: string | null;
  label: string;
  type: 'page' | 'collection' | 'external';
  targetId: string | null;
  targetSlug: string | null;
  externalUrl: string | null;
  sortOrder: number;
  groupLabel: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface NavMenu {
  id: string;
  name: string;
  slug: string;
}

interface CmsPageRef {
  id: string;
  slug: string;
  title: string;
  status: string;
}

interface CollectionTypeRef {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
}

interface NavItemForm {
  label: string;
  type: 'page' | 'collection' | 'external';
  targetId: string;
  targetSlug: string;
  externalUrl: string;
  groupLabel: string;
  isActive: boolean;
}

const EMPTY_FORM: NavItemForm = {
  label: '',
  type: 'page',
  targetId: '',
  targetSlug: '',
  externalUrl: '',
  groupLabel: '',
  isActive: true,
};

// ─── Type icons ────────────────────────────────────────────────────────────────

function TypeIcon({ type }: { type: NavItem['type'] }) {
  if (type === 'collection') return <Database size={12} className="text-indigo-400" />;
  if (type === 'external') return <ExternalLink size={12} className="text-orange-400" />;
  return <FileText size={12} className="text-zinc-400" />;
}

// ─── Sortable row ──────────────────────────────────────────────────────────────

function SortableNavRow({
  item,
  onToggle,
  onEdit,
  onDelete,
}: {
  item: NavItem;
  onToggle: (item: NavItem) => void;
  onEdit: (item: NavItem) => void;
  onDelete: (item: NavItem) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const targetDisplay =
    item.type === 'external'
      ? (item.externalUrl ?? '—')
      : item.targetSlug
        ? `/${item.type === 'collection' ? 'c' : 'p'}/${item.targetSlug}`
        : '—';

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b border-zinc-100 bg-white hover:bg-zinc-50 group"
    >
      <td className="px-3 py-3 w-8">
        <button
          {...attributes}
          {...listeners}
          className="text-zinc-300 hover:text-zinc-500 cursor-grab active:cursor-grabbing touch-none"
          tabIndex={-1}
        >
          <GripVertical size={14} />
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <TypeIcon type={item.type} />
          <span className="font-medium text-zinc-900 text-sm">{item.label}</span>
          {item.groupLabel && (
            <span className="text-[10px] font-medium bg-zinc-100 text-zinc-500 rounded px-1.5 py-0.5">
              {item.groupLabel}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge
          variant={
            item.type === 'page' ? 'default' : item.type === 'collection' ? 'info' : 'warning'
          }
        >
          {item.type}
        </Badge>
      </td>
      <td className="px-4 py-3 text-xs text-zinc-500 font-mono max-w-[180px] truncate">
        {targetDisplay}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center text-xs font-medium ${item.isActive ? 'text-emerald-600' : 'text-zinc-400'}`}
        >
          {item.isActive ? 'Visible' : 'Hidden'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onToggle(item)}
            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
            title={item.isActive ? 'Hide from nav' : 'Show in nav'}
          >
            {item.isActive ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
          <button
            onClick={() => onEdit(item)}
            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
            title="Edit"
          >
            <Pencil size={12} />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md"
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsNavPage() {
  const { spaceId, menuId } = useParams<{ spaceId: string; menuId?: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const BASE = `/api/v1/cms/spaces/${spaceId}/nav`;

  const [items, setItems] = useState<NavItem[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<NavItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NavItem | null>(null);
  const [form, setForm] = useState<NavItemForm>(EMPTY_FORM);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Queries ─────────────────────────────────────────────────────────────────

  const navUrl = menuId ? `${BASE}?menuId=${menuId}` : BASE;

  const { data: rawItems = [], isLoading } = useQuery<NavItem[]>({
    queryKey: ['cms-nav', spaceId, menuId],
    queryFn: () => api.get(navUrl).then((r) => r.data),
    enabled: !!spaceId,
  });

  const { data: menu } = useQuery<NavMenu | null>({
    queryKey: ['cms-nav-menu', spaceId, menuId],
    queryFn: () =>
      api.get(`/api/v1/cms/spaces/${spaceId}/nav/menus`).then((r) => {
        const menus = r.data as NavMenu[];
        return menus.find((m) => m.id === menuId) ?? null;
      }),
    enabled: !!spaceId && !!menuId,
  });

  const { data: pages = [] } = useQuery<CmsPageRef[]>({
    queryKey: ['cms-pages-refs', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/pages`).then((r) => r.data),
    enabled: !!spaceId,
  });

  const { data: collectionTypes = [] } = useQuery<CollectionTypeRef[]>({
    queryKey: ['cms-collections-refs', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/collections`).then((r) => r.data),
    enabled: !!spaceId,
  });

  // Sync local items state from server data (only when not dragging)
  useEffect(() => {
    setItems(rawItems);
  }, [rawItems]);

  // ── Mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (body: object) =>
      api.post(BASE, { ...body, ...(menuId ? { menuId } : {}) }).then((r) => r.data as NavItem),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-nav', spaceId, menuId] });
      setShowForm(false);
      setForm(EMPTY_FORM);
      toast.success('Nav item added');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to add nav item')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: string; [key: string]: unknown }) =>
      api.patch(`${BASE}/${id}`, body).then((r) => r.data as NavItem),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-nav', spaceId, menuId] });
      setShowForm(false);
      setEditTarget(null);
      setForm(EMPTY_FORM);
      toast.success('Nav item updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update nav item')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-nav', spaceId, menuId] });
      setDeleteTarget(null);
      toast.success('Nav item deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const reorderMutation = useMutation({
    mutationFn: (reorderedItems: Array<{ id: string; sortOrder: number }>) =>
      api.put(`${BASE}/order`, { items: reorderedItems }),
    onError: (err) => {
      // Revert on error
      setItems([...rawItems].sort((a, b) => a.sortOrder - b.sortOrder));
      toast.error(extractError(err, 'Failed to save order'));
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i.id === active.id);
      const newIndex = prev.findIndex((i) => i.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      // Save new order to server
      reorderMutation.mutate(reordered.map((item, idx) => ({ id: item.id, sortOrder: idx })));
      return reordered;
    });
  }

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(item: NavItem) {
    setEditTarget(item);
    setForm({
      label: item.label,
      type: item.type,
      targetId: item.targetId ?? '',
      targetSlug: item.targetSlug ?? '',
      externalUrl: item.externalUrl ?? '',
      groupLabel: item.groupLabel ?? '',
      isActive: item.isActive,
    });
    setShowForm(true);
  }

  function handleToggle(item: NavItem) {
    updateMutation.mutate({ id: item.id, isActive: !item.isActive });
  }

  function handleSubmit() {
    const body: Record<string, unknown> = {
      label: form.label.trim(),
      type: form.type,
      groupLabel: form.groupLabel.trim() || null,
      isActive: form.isActive,
    };

    if (form.type === 'external') {
      body.externalUrl = form.externalUrl.trim();
    } else {
      body.targetId = form.targetId || null;
      // Always re-resolve slug from selected target
      if (form.type === 'page') {
        const page = pages.find((p) => p.id === form.targetId);
        body.targetSlug = (page?.slug ?? form.targetSlug) || null;
      } else {
        const ct = collectionTypes.find((c) => c.id === form.targetId);
        body.targetSlug = (ct?.slug ?? form.targetSlug) || null;
      }
    }

    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, ...body });
    } else {
      createMutation.mutate(body);
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-zinc-400 mb-4">
        {menuId ? (
          <>
            <Link
              to={`/spaces/${spaceId}/menus`}
              className="hover:text-zinc-600 flex items-center gap-1"
            >
              <ChevronLeft size={13} />
              Menus
            </Link>
            {menu && (
              <>
                <span>/</span>
                <span className="text-zinc-700 font-medium">{menu.name}</span>
              </>
            )}
          </>
        ) : (
          <Link
            to={`/spaces/${spaceId}`}
            className="hover:text-zinc-600 flex items-center gap-1"
          >
            <ChevronLeft size={13} />
            Back to space
          </Link>
        )}
      </div>

      <PageHeader
        title={menu ? `${menu.name} — Items` : 'Navigation'}
        description="Configure the customer portal navigation. Drag rows to reorder."
        action={
          <Button onClick={openCreate} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Item
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={5} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Navigation size={26} />}
          title="No nav items yet"
          description="Add pages, collections, or external links to build your portal navigation"
          action={{ label: 'Add Item', onClick: openCreate }}
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-3 py-3 w-8" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Label
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Target
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Visibility
                  </th>
                  <th className="px-4 py-3 w-28" />
                </tr>
              </thead>
              <SortableContext
                items={items.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {items.map((item) => (
                    <SortableNavRow
                      key={item.id}
                      item={item}
                      onToggle={handleToggle}
                      onEdit={openEdit}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditTarget(null);
          setForm(EMPTY_FORM);
        }}
        title={editTarget ? 'Edit Nav Item' : 'Add Nav Item'}
      >
        <div className="space-y-4">
          <Input
            label="Label *"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="Blog"
          />

          <Select
            label="Type *"
            value={form.type}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                type: e.target.value as NavItemForm['type'],
                targetId: '',
                targetSlug: '',
                externalUrl: '',
              }))
            }
          >
            <option value="page">CMS Page</option>
            <option value="collection">Collection</option>
            <option value="external">External URL</option>
          </Select>

          {form.type === 'page' && (
            <Select
              label="Page *"
              value={form.targetId}
              onChange={(e) => {
                const id = e.target.value;
                const page = pages.find((p) => p.id === id);
                setForm((f) => ({ ...f, targetId: id, targetSlug: page?.slug ?? '' }));
              }}
            >
              <option value="">— Select a page —</option>
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} ({p.status})
                </option>
              ))}
            </Select>
          )}

          {form.type === 'collection' && (
            <Select
              label="Collection *"
              value={form.targetId}
              onChange={(e) => {
                const id = e.target.value;
                const ct = collectionTypes.find((c) => c.id === id);
                setForm((f) => ({ ...f, targetId: id, targetSlug: ct?.slug ?? '' }));
              }}
            >
              <option value="">— Select a collection —</option>
              {collectionTypes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}

          {form.type === 'external' && (
            <Input
              label="URL *"
              value={form.externalUrl}
              onChange={(e) => setForm((f) => ({ ...f, externalUrl: e.target.value }))}
              placeholder="https://example.com"
              type="url"
            />
          )}

          <Input
            label="Group / Section label"
            value={form.groupLabel}
            onChange={(e) => setForm((f) => ({ ...f, groupLabel: e.target.value }))}
            placeholder="Main, Footer, …"
          />

          <div className="flex items-center gap-3">
            <input
              id="nav-isActive"
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
            />
            <label htmlFor="nav-isActive" className="text-sm text-zinc-700 cursor-pointer">
              Visible in portal navigation
            </label>
          </div>
        </div>

        {(createMutation.isError || updateMutation.isError) && (
          <p className="text-xs text-red-500 mt-3">
            {extractError(createMutation.error ?? updateMutation.error)}
          </p>
        )}

        <ModalFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowForm(false);
              setEditTarget(null);
              setForm(EMPTY_FORM);
            }}
          >
            Cancel
          </Button>
          <Button
            loading={isSubmitting}
            disabled={
              !form.label.trim() ||
              (form.type !== 'external' && !form.targetId) ||
              (form.type === 'external' && !form.externalUrl.trim())
            }
            onClick={handleSubmit}
          >
            {editTarget ? 'Save Changes' : 'Add Item'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove nav item?"
        message={`"${deleteTarget?.label}" will be removed from the navigation.`}
        confirmLabel="Remove"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
